import { AmountUsd } from '@ui/components/Amount/AmountUsd';
import { Percentage } from '@ui/components/Amount/Percentage';
import { pageBackgroundStyle } from '@ui/components/Background/PageBackground';
import { BigIntAmount } from '@ui/components/BigIntAmount/BigIntAmount';
import { LinkExternal } from '@ui/components/icons';
import { useShouldShowMaintenanceBanner } from '@ui/components/Layout/hooks/useShouldShowMaintenanceBanner';
import { HLine } from '@ui/components/Line';
import { GIT_BOOK_AUDITS } from '@ui/constants/links';
import { ROUTES } from '@ui/constants/routes';
import { ExchangeMON, LVMON, sLVMON, WMON } from '@ui/constants/tokens';
import Exchange from '@ui/features/Exchange';
import { FastRedeemExecutor } from '@ui/features/Exchange/redeem/executors/FastRedeemExecutor';
import { StandardRedeemExecutor } from '@ui/features/Exchange/redeem/executors/StandardRedeemExecutor';
import { useDataChainId } from '@ui/hooks/datachain/useDataChainStore';
import { usePriceLatest } from '@ui/hooks/price/usePriceLatest';
import { TopNavBar } from '@ui/layouts/components/TopNavBar';
import { Button } from '@ui/lib/park/ui/button';
import { Icon } from '@ui/lib/park/ui/icon';
import { Label } from '@ui/lib/park/ui/styled/field';
import { Text } from '@ui/lib/park/ui/text';
import { TOKEN_ADDRESS_BY_CHAIN } from '@ui/services/contract/constants/address';
import { useTransparencyData } from '@ui/services/contract/hooks/LVToken/useTransparencyData';
import { useLpUnrealizedPnlUsd } from '@ui/services/contract/hooks/TradingCoreFacet/useLpUnrealizedPnlUsd';
import { getExplorerLink } from '@ui/utils/links';
import { shortenAddress } from '@ui/utils/formatAddress';
import {
  StakeProtocolAssetsV3,
  useLvmonTreasuryVault,
} from '@ui/views/LVMON/StakeProtocolAssetsV3';
import { StakeStatsV3 } from '@ui/views/LVMON/StakeStatsV3';
import BigNumber from 'bignumber.js';
import type { NextPage } from 'next';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { Box, Flex, HStack, styled, VStack } from 'styled-system/jsx';
import { erc20Abi } from 'viem';
import { useAccount, useBalance, useReadContract } from 'wagmi';

type ModuleTab = 'stake' | 'mint-redeem';

type PageProps = {
  initialModuleTab?: ModuleTab;
  initialExchangeTab?: 'Mint' | 'Redeem' | 'Fast Redeem';
};

const LVMON_DOCS_LINK =
  'https://leverup.gitbook.io/docs/liquidity-layer/mon-lvmon';

/** Header ghost button — transparent, 1px default border, strong on hover. */
const ghostBtnProps = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  h: '30px',
  px: '12px',
  borderRadius: '2px',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'v2.border.default',
  bg: 'transparent',
  color: 'v2.text.muted',
  textStyle: 'v2.label',
  cursor: 'pointer',
  textDecoration: 'none',
  _hover: { borderColor: 'v2.border.strong', color: 'v2.text.primary' },
} as const;

/** V2 card shell for the action card / info rail. */
const cardProps = {
  bg: 'v2.bg.panel',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'v2.border.default',
  borderRadius: '4px',
  p: '16px',
} as const;

/**
 * Page-scoped V2 overrides for the shared Exchange internals (their source
 * lives outside this view): underline sub-tabs sizing and amount-cell
 * surface/border colors. Style-only — no behavior change.
 */
const exchangeV2Css = {
  '& [data-scope=tabs][data-part=list]': { gap: '2px' },
  '& [data-scope=tabs][data-part=trigger]': {
    h: '34px',
    minW: '0',
    px: '12px',
    textStyle: 'v2.label',
  },
  '& [data-scope=field][data-part=root]': {
    background: 'v2.bg.surface1',
    borderColor: 'v2.border.default',
  },
  '& [data-scope=field][data-part=root]:focus-within': {
    borderColor: 'v2.border.focus',
  },
} as const;

/** 44px CTA per V2 spec (wraps the shared ExecuteButton). */
const ctaCss = {
  '& > button': {
    h: '44px',
    textStyle: 'v2.cta',
  },
} as const;

const StatCell = (props: {
  label: React.ReactNode;
  isFirst?: boolean;
  meta?: React.ReactNode;
  children: React.ReactNode;
}) => {
  const { label, isFirst, meta, children } = props;

  return (
    <Flex
      direction={{ base: 'row', desktop: 'column' }}
      alignItems={{ base: 'baseline', desktop: 'flex-start' }}
      justifyContent={{ base: 'space-between', desktop: 'flex-start' }}
      gap={{ base: '8px', desktop: '5px' }}
      px={{ base: '16px', desktop: '20px' }}
      py={{ base: '13px', desktop: '18px' }}
      borderTopWidth={{ base: isFirst ? '0px' : '1px', desktop: '0px' }}
      borderLeftWidth={{ base: '0px', desktop: isFirst ? '0px' : '1px' }}
      borderStyle="solid"
      borderColor="v2.border.default"
      minW={0}
    >
      <Text textStyle="v2.tableHeader" color="v2.text.subtle">
        {label}
      </Text>
      {children}
      {meta}
    </Flex>
  );
};

const ModuleTabButton = (props: {
  isActive: boolean;
  label: string;
  onClick: () => void;
}) => {
  const { isActive, label, onClick } = props;

  return (
    <styled.button
      type="button"
      onClick={onClick}
      h="30px"
      px="16px"
      borderRadius="2px"
      border="0"
      cursor="pointer"
      textStyle="v2.label"
      bg={isActive ? 'v2.bg.surface3' : 'transparent'}
      color={isActive ? 'v2.text.primary' : 'v2.text.muted'}
      _hover={isActive ? undefined : { color: 'v2.text.primary' }}
    >
      {label}
    </styled.button>
  );
};

export const Page: NextPage<PageProps> = ({
  initialModuleTab = 'stake',
  initialExchangeTab,
}) => {
  const { t } = useTranslation();
  const shouldShowMaintenanceBanner = useShouldShowMaintenanceBanner();
  const router = useRouter();
  const { data: transparencyData, isLoading } = useTransparencyData('LVMON');
  const { data: lpUnrealizedPnlUsdData } = useLpUnrealizedPnlUsd();
  const chainId = useDataChainId();
  const lvmonAddress = TOKEN_ADDRESS_BY_CHAIN[chainId].LVMON;
  const slvmonAddress = TOKEN_ADDRESS_BY_CHAIN[chainId].sLVMON;
  const [activeTab, setActiveTab] = useState<ModuleTab>(initialModuleTab);
  const { address } = useAccount();
  const { treasuryVaultAddress } = useLvmonTreasuryVault();

  const lpUnrealizedPnlUsd =
    lpUnrealizedPnlUsdData?.totalUnrealizedPnlUsd || BigNumber(0);
  const lvTokenCirculatingSupply =
    transparencyData?.lvTokenCirculatingSupply || 0n;
  const { data: lvmonBalanceInSlvmon } = useReadContract({
    abi: erc20Abi,
    address: lvmonAddress,
    functionName: 'balanceOf',
    args: [slvmonAddress],
    query: {
      enabled: !!lvmonAddress && !!slvmonAddress,
    },
  });
  const displayCirculatingSupply =
    lvTokenCirculatingSupply + (lvmonBalanceInSlvmon ?? 0n);
  const totalValutValue = transparencyData?.totalValutValue || 0n;

  const monPrice = usePriceLatest('MON');
  const totalValutValueUsd = monPrice
    ? BigNumber(totalValutValue.toString())
        .div(BigNumber(10).pow(18))
        .multipliedBy(monPrice)
    : undefined;
  const lvTokenCirculatingSupplyUsd = monPrice
    ? BigNumber(displayCirculatingSupply.toString())
        .div(BigNumber(10).pow(18))
        .multipliedBy(monPrice)
    : undefined;

  const collateralRatio =
    lvTokenCirculatingSupply > 0n
      ? parseInt(
          ((totalValutValue * 10000n) / lvTokenCirculatingSupply).toString(),
        ) / 10000
      : 0;

  const { data: lvmonBalanceData } = useBalance({
    address,
    token: lvmonAddress,
    query: {
      enabled: !!address && !!lvmonAddress,
    },
  });

  const lvmonPrice = monPrice ? BigNumber(monPrice) : undefined;
  const totalSupplyFormatted = BigNumber(lvTokenCirculatingSupply.toString())
    .div(BigNumber(10).pow(18))
    .toFormat(2);
  const userBalanceFormatted = lvmonBalanceData
    ? BigNumber(lvmonBalanceData.formatted).toFormat(2)
    : '-';
  const backingRatioFormatted = `${BigNumber(collateralRatio)
    .multipliedBy(100)
    .toFormat(2)}%`;

  const mintStatCells: {
    label: string;
    value: string;
    color?: string;
  }[] = [
    {
      label: t('lvmon.mint.price', 'LVMON Price'),
      value: lvmonPrice ? lvmonPrice.toFormat(5) : '-',
    },
    {
      label: t('lvmon.mint.totalSupply', 'Total Supply'),
      value: totalSupplyFormatted,
    },
    {
      label: t('lvmon.mint.yourBalance', 'Your Balance'),
      value: `${userBalanceFormatted} LVMON`,
    },
    {
      label: t('lvmon.mint.backingRatio', 'Backing Ratio'),
      value: backingRatioFormatted,
      color: 'price.priceUp',
    },
  ];

  return (
    <>
      <TopNavBar />
      <VStack
        style={pageBackgroundStyle}
        minH={
          shouldShowMaintenanceBanner
            ? 'layout.contentHeightWithAnnouncement'
            : 'layout.contentHeight'
        }
      >
        <Flex
          direction="column"
          w="full"
          maxW="1120px"
          mx="auto"
          px={{ base: '12px', tablet: '24px' }}
          pt={{ base: '20px', tablet: '28px' }}
          pb={{ base: '32px', tablet: '48px' }}
          gap={{ base: '12px', tablet: '16px' }}
        >
          {/* ---- Page header ---- */}
          <Flex
            alignItems="flex-start"
            gap="14px"
            flexWrap={{ base: 'wrap', tablet: 'nowrap' }}
          >
            <styled.img
              src="/tokens/LVMON.svg"
              width="40px"
              height="40px"
              flexShrink={0}
            />
            <VStack alignItems="flex-start" gap="3px" minW={0}>
              <Text
                as="h1"
                fontSize={{ base: '20px', tablet: '24px' }}
                fontWeight="600"
                letterSpacing="-0.015em"
                lineHeight="1.25"
                color="v2.text.primary"
              >
                {t('lvmon.head.title', 'LeverUp MON')}{' '}
                <styled.span color="v2.text.subtle" fontWeight="500">
                  · LVMON
                </styled.span>
              </Text>
              <Text
                fontSize="13px"
                lineHeight="1.45"
                color="v2.text.muted"
                maxW="60ch"
              >
                {t(
                  'lvmon.head.subtitle',
                  'Introducing LVMON, the MON ecosystem synthetic token at the core of LeverUp. All positions are denominated and settled in LVMON. Use MON as collateral to mint LVMON.',
                )}
              </Text>
            </VStack>
            <HStack
              gap="8px"
              flexShrink={0}
              ml={{ base: '0', tablet: 'auto' }}
              w={{ base: 'full', tablet: 'auto' }}
            >
              <styled.button
                type="button"
                {...ghostBtnProps}
                onClick={() => {
                  window.open(
                    getExplorerLink(lvmonAddress, 'token', chainId),
                    '_blank',
                  );
                }}
              >
                <styled.span textStyle="numbers.xsmall">
                  {shortenAddress({ address: lvmonAddress })}
                </styled.span>
                <Icon boxSize="11px">
                  <LinkExternal />
                </Icon>
              </styled.button>
              <styled.a
                {...ghostBtnProps}
                href={LVMON_DOCS_LINK}
                target="_blank"
                rel="noreferrer"
              >
                {t('lvmon.head.docs', 'Docs')}
                <Icon boxSize="11px">
                  <LinkExternal />
                </Icon>
              </styled.a>
            </HStack>
          </Flex>

          {/* ---- Stat strip ---- */}
          <Box
            bg="v2.bg.panel"
            borderWidth="1px"
            borderStyle="solid"
            borderColor="v2.border.default"
            borderRadius="4px"
            display="grid"
            gridTemplateColumns={{ base: '1fr', desktop: 'repeat(3, 1fr)' }}
          >
            <StatCell
              isFirst
              label={t('lvmon.stats.vaultValue', 'Protocol Vault Value')}
              meta={
                totalValutValueUsd && (
                  <AmountUsd
                    before="≈ "
                    textStyle="numbers.xsmall"
                    color="v2.text.subtle"
                    display={{ base: 'none', desktop: 'block' }}
                    value={totalValutValueUsd}
                    opts={{ kmb: true }}
                  />
                )
              }
            >
              <HStack alignItems="baseline" gap="4px">
                <BigIntAmount
                  textStyle="numbers.xxlarge"
                  fontSize={{ base: '17px', desktop: '28px' }}
                  letterSpacing="-0.015em"
                  color="v2.text.primary"
                  opts={{ decimals: 18, autoKmb: true }}
                  value={totalValutValue}
                />
                <Text fontSize="13px" fontWeight="500" color="v2.text.subtle">
                  MON
                </Text>
              </HStack>
            </StatCell>

            <StatCell
              label={t('lvmon.stats.circulating', 'Circulating LVMON')}
              meta={
                lvTokenCirculatingSupplyUsd && (
                  <AmountUsd
                    before="≈ "
                    textStyle="numbers.xsmall"
                    color="v2.text.subtle"
                    display={{ base: 'none', desktop: 'block' }}
                    value={lvTokenCirculatingSupplyUsd}
                    opts={{ kmb: true }}
                  />
                )
              }
            >
              <BigIntAmount
                textStyle="numbers.xxlarge"
                fontSize={{ base: '17px', desktop: '28px' }}
                letterSpacing="-0.015em"
                color="v2.text.primary"
                opts={{ decimals: 18, autoKmb: true }}
                value={displayCirculatingSupply}
              />
            </StatCell>

            <StatCell
              label={t('lvmon.stats.collateralRatio', 'Collateral Ratio')}
              meta={
                !isLoading &&
                collateralRatio >= 1 && (
                  <Text
                    textStyle="numbers.xsmall"
                    fontWeight="400"
                    color="v2.text.subtle"
                    display={{ base: 'none', desktop: 'block' }}
                  >
                    {t(
                      'lvmon.stats.collateralHealthy',
                      'healthy — fully backed above 100%',
                    )}
                  </Text>
                )
              }
            >
              <Percentage
                textStyle="numbers.xxlarge"
                fontSize={{ base: '17px', desktop: '28px' }}
                letterSpacing="-0.015em"
                color={
                  !isLoading
                    ? collateralRatio < 1
                      ? 'price.priceDown'
                      : 'price.priceUp'
                    : ''
                }
                opts={{}}
                value={collateralRatio}
              />
            </StatCell>
          </Box>

          {/* ---- Two columns: action card + info rail ---- */}
          <Box
            display="grid"
            gridTemplateColumns={{ base: '1fr', desktop: '2fr 1fr' }}
            gap="8px"
            alignItems="start"
          >
            {/* Action card */}
            <Flex direction="column" gap="12px" minW={0} {...cardProps}>
              <HStack
                gap="2px"
                p="2px"
                alignSelf="flex-start"
                bg="v2.bg.surface1"
                borderWidth="1px"
                borderStyle="solid"
                borderColor="v2.border.default"
                borderRadius="2px"
              >
                <ModuleTabButton
                  isActive={activeTab === 'stake'}
                  label={t('lvmon.moduleTab.stake', 'Stake')}
                  onClick={() => setActiveTab('stake')}
                />
                <ModuleTabButton
                  isActive={activeTab === 'mint-redeem'}
                  label={t('lvmon.moduleTab.mintRedeem', 'Mint & Redeem')}
                  onClick={() => setActiveTab('mint-redeem')}
                />
              </HStack>

              {activeTab === 'mint-redeem' ? (
                <Box css={exchangeV2Css}>
                  <Exchange.Root defaultValue={initialExchangeTab}>
                    <Exchange.Context
                      label="Mint"
                      defaultInputToken={ExchangeMON()}
                      defaultOutputToken={LVMON(chainId)}
                      overrideInputTokens={[ExchangeMON()]}
                      overrideOutputTokens={[ExchangeMON(), LVMON(chainId)]}
                    >
                      <VStack gap={4} width="full" alignItems="stretch" pt={2}>
                        <VStack alignItems="flex-start" gap={1}>
                          <HStack gap={2} alignItems="center">
                            <styled.img
                              src="/tokens/LVMON.svg"
                              width="40px"
                              height="40px"
                            />
                            <styled.img
                              src="/icons/lvmon-full.svg"
                              width="150px"
                              height="40px"
                            />
                          </HStack>
                          <Text
                            fontSize="12px"
                            color="v2.text.muted"
                            fontWeight={500}
                            lineHeight="18px"
                          >
                            {t(
                              'lvmon.mint.desc',
                              'Open with MON collateral, settle in LVMON',
                            )}
                          </Text>
                        </VStack>
                        <Box
                          borderWidth="1px"
                          borderStyle="solid"
                          borderColor="v2.border.default"
                          borderRadius="4px"
                          bg="v2.bg.surface1"
                          w="full"
                          alignSelf="center"
                        >
                          <Box display="grid" gridTemplateColumns="1fr 1fr">
                            {mintStatCells.map((cell, index) => (
                              <VStack
                                key={cell.label}
                                alignItems="flex-start"
                                gap={1}
                                px={3}
                                py={3}
                                borderRightWidth={
                                  index % 2 === 0 ? '1px' : '0px'
                                }
                                borderBottomWidth={index < 2 ? '1px' : '0px'}
                                borderStyle="solid"
                                borderColor="v2.border.default"
                              >
                                <Text
                                  textStyle="v2.meta"
                                  fontWeight="400"
                                  color="v2.text.subtle"
                                >
                                  {cell.label}
                                </Text>
                                <Text
                                  textStyle="numbers.default"
                                  color={cell.color ?? 'v2.text.primary'}
                                >
                                  {cell.value}
                                </Text>
                              </VStack>
                            ))}
                          </Box>
                        </Box>
                      </VStack>
                      <Button
                        onClick={() => {
                          router.push(ROUTES.INDEX);
                        }}
                        mt={4}
                        width="full"
                        h="44px"
                        textStyle="v2.cta"
                      >
                        {t('lvmon.mint.openPosition', 'Open Position')}
                      </Button>
                    </Exchange.Context>
                    <Exchange.Redeem.StandardRedeemContext
                      label="Redeem"
                      defaultInputToken={LVMON(chainId)}
                      defaultOutputToken={WMON(chainId)}
                      overrideInputTokens={[LVMON(chainId)]}
                      overrideOutputTokens={[WMON(chainId), LVMON(chainId)]}
                      executor={StandardRedeemExecutor}
                    >
                      <Exchange.Redeem.StandardRedeem />
                    </Exchange.Redeem.StandardRedeemContext>
                    <Exchange.Context
                      label="Fast Redeem"
                      defaultInputToken={LVMON(chainId)}
                      defaultOutputToken={WMON(chainId)}
                      overrideInputTokens={[LVMON(chainId)]}
                      overrideOutputTokens={[WMON(chainId), LVMON(chainId)]}
                      executor={FastRedeemExecutor}
                    >
                      <Exchange.Redeem.FastRedeem />
                    </Exchange.Context>
                  </Exchange.Root>
                </Box>
              ) : (
                <VStack gap={3} alignItems="stretch">
                  <StakeStatsV3 />
                  <Box css={exchangeV2Css}>
                    <Exchange.Root>
                      <Exchange.Context
                        label="Stake"
                        defaultInputToken={LVMON(chainId)}
                        defaultOutputToken={sLVMON(chainId)}
                        overrideInputTokens={[LVMON(chainId)]}
                        overrideOutputTokens={[sLVMON(chainId), LVMON(chainId)]}
                      >
                        <VStack gap={1} w="full">
                          <Exchange.Input />
                          <Exchange.LST.LimiterWarning />
                          <Exchange.LST.MyPosition />
                        </VStack>
                        <Box w="full" css={ctaCss}>
                          <Exchange.ExecuteButton label="Stake" />
                        </Box>
                      </Exchange.Context>
                      <Exchange.Context
                        label="Unstake"
                        defaultInputToken={sLVMON(chainId)}
                        defaultOutputToken={LVMON(chainId)}
                        overrideInputTokens={[sLVMON(chainId)]}
                        overrideOutputTokens={[LVMON(chainId), sLVMON(chainId)]}
                      >
                        <VStack gap={1} w="full">
                          <Exchange.LST.UnstakeInput />
                          <HLine mt={4} w="full" />
                          <Exchange.TextOutput />
                          <Exchange.LST.TextUnstakeFees />
                        </VStack>
                        <Box w="full" css={ctaCss}>
                          <Exchange.ExecuteButton label="Unstake" />
                        </Box>
                      </Exchange.Context>
                      <Exchange.Context
                        label="Swap"
                        defaultInputToken={ExchangeMON()}
                        defaultOutputToken={LVMON(chainId)}
                        overrideInputTokens={[ExchangeMON()]}
                        overrideOutputTokens={[ExchangeMON(), LVMON(chainId)]}
                      >
                        <VStack gap={1} w="full">
                          <Exchange.InfoCard>
                            <Label>Market Price:</Label>
                            <Exchange.MarketPrice />
                          </Exchange.InfoCard>
                          <Exchange.Input />
                          <Exchange.SwitchButton />
                          <Exchange.Output />
                        </VStack>
                        <Box w="full" css={ctaCss}>
                          <Exchange.ExecuteButton label="Swap" />
                        </Box>
                      </Exchange.Context>
                    </Exchange.Root>
                  </Box>
                </VStack>
              )}
            </Flex>

            {/* Info rail */}
            <Flex direction="column" gap="8px" minW={0}>
              <StakeProtocolAssetsV3 />
              <Flex direction="column" gap="12px" {...cardProps}>
                <Text textStyle="v2.sectionLabel" color="v2.text.subtle">
                  {t('lvmon.howItWorks.title', 'How it works')}
                </Text>
                <Text
                  fontFamily="Inter"
                  fontSize="11px"
                  fontWeight="500"
                  lineHeight="1.6"
                  color="v2.text.subtle"
                >
                  {t(
                    'lvmon.howItWorks.body',
                    'Vault MON is delegated to Monad validators through interest-bearing assets. POS rewards stream back to LVMON stakers each cycle. Rewards compound automatically once claimed.',
                  )}
                </Text>
              </Flex>
            </Flex>
          </Box>

          {/* ---- Page footer meta ---- */}
          <HStack gap="18px" flexWrap="wrap" alignItems="center">
            <Text
              textStyle="numbers.xsmall"
              fontWeight="400"
              color="v2.text.subtle"
            >
              LVMON · {shortenAddress({ address: lvmonAddress, leadingChars: 6 })}
            </Text>
            <styled.a
              href={GIT_BOOK_AUDITS}
              target="_blank"
              rel="noreferrer"
              textStyle="numbers.xsmall"
              fontWeight="400"
              color="v2.text.subtle"
              textDecoration="none"
              _hover={{ color: 'v2.text.secondary', textDecoration: 'underline' }}
            >
              {t('lvmon.footer.audit', 'Audit')} ↗
            </styled.a>
            {treasuryVaultAddress && (
              <styled.a
                href={getExplorerLink(
                  treasuryVaultAddress,
                  'stake-portfolio',
                  chainId,
                )}
                target="_blank"
                rel="noreferrer"
                textStyle="numbers.xsmall"
                fontWeight="400"
                color="v2.text.subtle"
                textDecoration="none"
                _hover={{
                  color: 'v2.text.secondary',
                  textDecoration: 'underline',
                }}
              >
                {t('lvmon.footer.validatorSet', 'Validator set')} ↗
              </styled.a>
            )}
          </HStack>
        </Flex>
      </VStack>
    </>
  );
};
